import { createSlice } from '@reduxjs/toolkit';
import { groupBy, uniqBy } from 'lodash';
import { ADDRESS_DEFAULT_COLUMNS } from 'src/app/AddressBook/addressBook.const';
import { filterServicesByType } from 'src/app/AddressBook/addressBook.helpers';
import { INVOICE_DEFAULT_COLUMNS, POD_DEFAULT_COLUMNS } from 'src/app/DocumentHub/documentHub.const';
import { CP_QUOTE_DEFAULT_COLUMNS } from 'src/app/MyQuote/myQuote.const';
import { CP_ORDER_DEFAULT_COLUMNS } from 'src/app/Orders/order.const';
import { TRACK_DEFAULT_COLUMNS } from 'src/app/Track/track.const';
import { listEquipmentTypes, listHandlingUnit, listLoadboardCommodities, listService } from 'src/services/CommonService';
import { fetchOrganizationMemberList } from 'src/services/OrganizationService';
import { getSelectBoxOptions } from 'src/utils/CommonFunctions';
import { fetchJsFromCDN } from 'src/utils/socketUtils';
import { resolveViewerTimezone, setResolvedViewerTimezone } from 'src/utils/viewerTimezone';

import { CURRENCY, MAP_API, TIMEZONE, TRIMBLE_KEY } from '../constants/common';

// Module-level socket state — NOT stored in Redux.
// Socket objects are not serializable and Immer's proxy wrapping breaks their methods.
let socketInstance: any = null;
let isConnecting = false;

import type { RootState } from './store';

interface BasicState {
    currency: string;
    appearance: any;
    mapService: string;
    googleKey: string;
    trimbleKey: string;
    tooltipGlobalOpen: boolean;
    lastSyncDateTimeActual: any;
    allTimeStartDateActual: any;
    actionTooltipGlobalOpen: boolean;
    actionDelayTimeout: NodeJS.Timeout | null;
    ordersUpdateSocketTriggeredCount: boolean | undefined;
    reportMonth: string;
    reportYear: string;
    additionalServices: {
        pickup: any[];
        delivery: any[];
        other: any[];
    };
    // Service-related state
    isServiceLoading: boolean;
    services: any[];
    servicesList: any[];
    serviceTypes: any;
    handlingUnitOptions: any[];
    isHandlingUnitLoading: boolean;
    vehicleTypeOptions: any[];
    isVehicleTypeLoading: boolean;
    vehicleAttrOptions: any[];
    isVehicleAttrLoading: boolean;
    vehicleSizeOptions: any[];
    // Commodity-related state
    commodityOptions: any[];
    isCommodityLoading: boolean;
    // Map-related state
    isMapLoading: boolean;
    googleMapStatus: boolean;
    allTeamListOptions: any[];
    allTeamMemberListOptions: any[];
    isTeamLoading: boolean;
    // Socket-related state
    isSocketConnected: boolean;
    socketIo: any;
    isAgentAvailable: boolean;
    customerOnlineStatus: boolean | null; // null = unknown, true = online, false = offline
    isUserRoomJoined: boolean;
    // Column settings — loaded at app boot via /common/init to avoid per-page flash
    quoteColumns: any[];
    orderColumns: any[];
    invoiceColumns: any[];
    podColumns: any[];
    addressColumns: any[];
    trackColumns: any[];
    /** Order detail → Route Overview table (alias cp_order_route_overview). [] = use ROUTE_OVERVIEW_DEFAULT_COLUMNS. */
    orderRouteOverviewColumns: any[];
    isInitLoaded: boolean;
    /**
     * True once GET /users/get-user-details has SETTLED for this page load (success or failure).
     *
     * Deliberately lives in `basic` and not in `auth`: `auth` is in the redux-persist whitelist, so
     * on a hard refresh isAuthenticated/currentUser/loader are all rehydrated from localStorage
     * before the token has been checked. `basic` is not persisted, so this reliably starts false on
     * every page load — which is exactly the "token not verified yet" signal we need.
     */
    isAuthVerified: boolean;
}

export const initialState: BasicState = {
    currency: CURRENCY.CAD,
    // `appearance.timezone` is the READER's clock (resolved in utils/viewerTimezone.ts), not the
    // organisation's; the organisation's own clock is kept as `appearance.orgTimezone`.
    appearance: { timezone: resolveViewerTimezone({ orgTimezone: TIMEZONE }), orgTimezone: TIMEZONE || null },
    mapService: MAP_API,
    googleKey: window.GOOGLE_API_KEY,
    trimbleKey: TRIMBLE_KEY,
    tooltipGlobalOpen: false,
    lastSyncDateTimeActual: undefined,
    allTimeStartDateActual: undefined,
    actionTooltipGlobalOpen: false,
    actionDelayTimeout: null,
    ordersUpdateSocketTriggeredCount: false,
    reportMonth: '',
    reportYear: '',
    additionalServices: {
        pickup: [],
        delivery: [],
        other: [],
    },
    // Service-related state
    isServiceLoading: false,
    services: [],
    servicesList: [],
    serviceTypes: {},
    handlingUnitOptions: [],
    isHandlingUnitLoading: false,
    vehicleTypeOptions: [],
    isVehicleTypeLoading: false,
    vehicleAttrOptions: [],
    isVehicleAttrLoading: false,
    vehicleSizeOptions: [],
    // Commodity-related state
    commodityOptions: [],
    isCommodityLoading: false,
    // Map-related state
    isMapLoading: false,
    googleMapStatus: false,
    allTeamListOptions: [],
    allTeamMemberListOptions: [],
    isTeamLoading: false,
    // Socket-related state
    isSocketConnected: false,
    socketIo: null,
    isAgentAvailable: false,
    customerOnlineStatus: null, // null = unknown status
    isUserRoomJoined: false,
    // Column settings — loaded at app boot, defaults used until /common/init resolves
    quoteColumns: CP_QUOTE_DEFAULT_COLUMNS,
    orderColumns: CP_ORDER_DEFAULT_COLUMNS,
    invoiceColumns: INVOICE_DEFAULT_COLUMNS,
    podColumns: POD_DEFAULT_COLUMNS,
    addressColumns: ADDRESS_DEFAULT_COLUMNS,
    trackColumns: TRACK_DEFAULT_COLUMNS,
    orderRouteOverviewColumns: [],
    isInitLoaded: false,
    isAuthVerified: false,
};

const basicSlice = createSlice({
    name: 'basic',
    initialState,
    reducers: {
        setCurrency: (state, action) => {
            state.currency = action.payload;
        },
        setAppearance: (state, action) => {
            // Payload is the organisation appearance row (its `timezone` is WAL's clock) plus an
            // optional `viewerTimezone` (the portal user's stored timezone). What every screen reads
            // as `appearance.timezone` is the resolved READER's clock: user, browser, organisation, UTC.
            const orgTimezone = action?.payload?.orgTimezone || action?.payload?.timezone || TIMEZONE || null;
            const timezone = resolveViewerTimezone({ userTimezone: action?.payload?.viewerTimezone, orgTimezone });
            setResolvedViewerTimezone(timezone);

            state.appearance = {
                ...action?.payload,
                orgTimezone,
                timezone,
            };
        },
        setMapService: (state, action) => {
            state.mapService = action.payload;
        },
        setGoogleKey: (state, action) => {
            state.googleKey = action.payload;
        },
        setTrimbleKey: (state, action) => {
            state.trimbleKey = action.payload;
        },
        setTooltipGlobalOpen: (state, action) => {
            state.tooltipGlobalOpen = action.payload;
        },
        setLastSyncDateTimeActual: (state, action) => {
            state.lastSyncDateTimeActual = action.payload;
        },
        setAllTimeStartDateActual: (state, action) => {
            state.allTimeStartDateActual = action.payload;
        },
        setActionTooltipGlobalOpen: (state, action) => {
            state.actionTooltipGlobalOpen = action.payload;
        },
        setActionDelayTimeout: (state, action) => {
            state.actionDelayTimeout = action.payload;
        },
        setOrdersUpdateSocketTriggeredCount: (state, action) => {
            state.ordersUpdateSocketTriggeredCount = action.payload;
        },
        setReportMonth: (state, action) => {
            state.reportMonth = action.payload;
        },
        setReportYear: (state, action) => {
            state.reportYear = action.payload;
        },
        setAdditionalServices: (state, action) => {
            state.additionalServices = action.payload;
        },
        clearAdditionalServices: (state) => {
            state.additionalServices = initialState.additionalServices;
        },
        // Service-related reducers
        setIsServiceLoading: (state, action) => {
            state.isServiceLoading = action.payload;
        },
        setServices: (state, action) => {
            state.services = action.payload;
        },
        setServicesList: (state, action) => {
            state.servicesList = action.payload;
        },
        setServiceTypes: (state, action) => {
            state.serviceTypes = action.payload;
        },
        setHandlingUnitOptions: (state, action) => {
            state.handlingUnitOptions = action.payload;
        },
        setIsHandlingUnitLoading: (state, action) => {
            state.isHandlingUnitLoading = action.payload;
        },
        setVehicleTypeOptions: (state, action) => {
            state.vehicleTypeOptions = action.payload;
        },
        setIsVehicleTypeLoading: (state, action) => {
            state.isVehicleTypeLoading = action.payload;
        },
        setVehicleAttrOptions: (state, action) => {
            state.vehicleAttrOptions = action.payload;
        },
        setIsVehicleAttrLoading: (state, action) => {
            state.isVehicleAttrLoading = action.payload;
        },
        setVehicleSizeOptions: (state, action) => {
            state.vehicleSizeOptions = action.payload;
        },
        // Commodity-related reducers
        setCommodityOptions: (state, action) => {
            state.commodityOptions = action.payload;
        },
        setIsCommodityLoading: (state, action) => {
            state.isCommodityLoading = action.payload;
        },
        // Map-related reducers
        setIsMapLoading: (state, action) => {
            state.isMapLoading = action.payload;
        },
        setGoogleMapStatus: (state, action) => {
            state.googleMapStatus = action.payload;
        },
        setAllTeamListOptions: (state, action) => {
            state.allTeamListOptions = action.payload;
        },
        setAllTeamMemberListOptions: (state, action) => {
            state.allTeamMemberListOptions = action.payload;
        },
        /**
         * Adds or updates members in the cached list, by id. Accepts a single member or an array
         * (invite creates several at once). Lets the Organization page keep this list correct
         * after a create/update without anyone re-fetching /organization/members — that list is
         * loaded once by /common/init and read from redux everywhere else.
         */
        upsertTeamMembers: (state, action) => {
            const incoming = Array.isArray(action.payload) ? action.payload : [action.payload];

            incoming.forEach((member: any) => {
                if (member?.id == null) return;

                const index = state.allTeamMemberListOptions.findIndex((m: any) => String(m?.id) === String(member.id));

                if (index >= 0) {
                    // Merge, never replace: the edit response carries only the changed fields,
                    // while the filter dropdowns read firstName/lastName/image off this row.
                    state.allTeamMemberListOptions[index] = { ...state.allTeamMemberListOptions[index], ...member };
                } else {
                    state.allTeamMemberListOptions.push(member);
                }
            });
        },
        /** Drops a member from the cached list after the Organization page deletes them. */
        removeTeamMember: (state, action) => {
            state.allTeamMemberListOptions = state.allTeamMemberListOptions.filter((m: any) => String(m?.id) !== String(action.payload));
        },
        setIsTeamLoading: (state, action) => {
            state.isTeamLoading = action.payload;
        },
        // Socket-related reducers
        setIsSocketConnected: (state, action) => {
            state.isSocketConnected = action.payload;
        },
        setSocketIo: (state, action) => {
            state.socketIo = action.payload;
        },
        setIsAgentAvailable: (state, action) => {
            state.isAgentAvailable = action.payload;
        },
        setCustomerOnlineStatus: (state, action) => {
            state.customerOnlineStatus = action.payload;
        },
        setIsUserRoomJoined: (state, action) => {
            state.isUserRoomJoined = action.payload;
        },
        setQuoteColumns: (state, action) => {
            state.quoteColumns = action.payload;
        },
        setOrderColumns: (state, action) => {
            state.orderColumns = action.payload;
        },
        setInvoiceColumns: (state, action) => {
            state.invoiceColumns = action.payload;
        },
        setPodColumns: (state, action) => {
            state.podColumns = action.payload;
        },
        setAddressColumns: (state, action) => {
            state.addressColumns = action.payload;
        },
        setTrackColumns: (state, action) => {
            state.trackColumns = action.payload;
        },
        setOrderRouteOverviewColumns: (state, action) => {
            state.orderRouteOverviewColumns = action.payload;
        },
        setIsInitLoaded: (state, action) => {
            state.isInitLoaded = action.payload;
        },
        setIsAuthVerified: (state, action) => {
            state.isAuthVerified = action.payload;
        },
        resetBasic: () => initialState,
    },
});

export const {
    setCurrency,
    setAppearance,
    setMapService,
    setGoogleKey,
    setTrimbleKey,
    setTooltipGlobalOpen,
    setLastSyncDateTimeActual,
    setAllTimeStartDateActual,
    setActionTooltipGlobalOpen,
    setActionDelayTimeout,
    setOrdersUpdateSocketTriggeredCount,
    setReportMonth,
    setReportYear,
    setAdditionalServices,
    clearAdditionalServices,
    // Service-related actions
    setIsServiceLoading,
    setServices,
    setServicesList,
    setServiceTypes,
    setHandlingUnitOptions,
    setIsHandlingUnitLoading,
    setVehicleTypeOptions,
    setIsVehicleTypeLoading,
    setVehicleAttrOptions,
    setIsVehicleAttrLoading,
    setVehicleSizeOptions,
    // Commodity-related actions
    setCommodityOptions,
    setIsCommodityLoading,
    setIsTeamLoading,
    setAllTeamListOptions,
    setAllTeamMemberListOptions,
    upsertTeamMembers,
    removeTeamMember,
    // Map-related actions
    setIsMapLoading,
    setGoogleMapStatus,
    // Socket-related actions
    setIsSocketConnected,
    setSocketIo,
    setIsAgentAvailable,
    setCustomerOnlineStatus,
    setIsUserRoomJoined,
    setQuoteColumns,
    setOrderColumns,
    setInvoiceColumns,
    setPodColumns,
    setAddressColumns,
    setTrackColumns,
    setOrderRouteOverviewColumns,
    setIsInitLoaded,
    setIsAuthVerified,
    resetBasic,
} = basicSlice.actions;

/**
 * Binds the /common/init `services` payload into Redux.
 *
 * Extracted so the single boot call and the standalone refetch below derive services,
 * serviceTypes, additionalServices and vehicleAttrOptions through the exact same mapping.
 */
export const applyServicesData = (dispatch: any, data: any[]) => {
    if (!Array.isArray(data)) return;

    if (data.length) {
        const groupedServices = filterServicesByType(data);
        dispatch(setAdditionalServices(groupedServices));
    }

    const formattedList = data.map((item: any) => ({
        id: item.id,
        label: item.name,
        value: item.slug,
    }));

    formattedList.push({
        id: '',
        label: 'Other',
        value: 'other',
    });

    dispatch(setServicesList(formattedList));
    dispatch(setServices(data));

    const serviceByGroup = groupBy(data, (val) => val.type);
    dispatch(setServiceTypes(serviceByGroup));

    const vehicleAttrOptions = data
        .filter((vt: any) => vt.isOnlyForFTL || vt.loadLinkName?.toLowerCase() === 'hazmat')
        .map((vt: any) => ({
            label: vt.loadLinkName,
            value: vt.loadLinkTerm,
            truckstop: vt.truckstop,
        }));
    dispatch(setVehicleAttrOptions(vehicleAttrOptions));
};

/**
 * Standalone refetch of /services/get.
 * NOT called on page load — app-boot data comes from /common/init. Kept for explicit refreshes.
 */
export const fetchServices = (): any => async (dispatch: any) => {
    dispatch(setIsServiceLoading(true));
    dispatch(setIsVehicleAttrLoading(true));

    try {
        const response = await listService({});

        if (response?.data && response?.status) {
            applyServicesData(dispatch, response.data);
        }
    } catch (error) {
        console.error('Error fetching services:', error);
    } finally {
        dispatch(setIsServiceLoading(false));
        dispatch(setIsVehicleAttrLoading(false));
    }
};

/** Binds the /common/init `handlingUnits` payload into Redux. */
export const applyHandlingUnitsData = (dispatch: any, data: any[]) => {
    if (Array.isArray(data) && data.length) {
        dispatch(setHandlingUnitOptions(getSelectBoxOptions(data, 'id', 'name')));
    } else {
        dispatch(setHandlingUnitOptions([]));
    }
};

/** Standalone refetch of /handling-units/list. NOT called on page load — see /common/init. */
export const fetchHandlingUnits = () => async (dispatch: any) => {
    dispatch(setIsHandlingUnitLoading(true));

    try {
        const result = await listHandlingUnit();

        applyHandlingUnitsData(dispatch, result?.data);
    } catch (error) {
        console.error('Error loading handling units:', error);
        dispatch(setHandlingUnitOptions([]));
    } finally {
        dispatch(setIsHandlingUnitLoading(false));
    }
};

/** Binds the /common/init `equipment` payload ({ vehicleTypes, vehicleSizes }) into Redux. */
export const applyEquipmentData = (dispatch: any, data: any) => {
    if (!data) return;

    const updatedListVehicleTypes = (data.vehicleTypes ?? []).map((vt: any) => ({
        label: vt.detail,
        value: vt.code,
        loadboard123: vt.loadboard123,
        dat: vt.dat,
        truckstop: vt.truckstop,
    }));
    dispatch(setVehicleTypeOptions(updatedListVehicleTypes));

    const updatedListVehicleSizes = (data.vehicleSizes ?? []).map((vt: any) => ({
        label: vt.detail,
        value: vt.code,
    }));
    dispatch(setVehicleSizeOptions(updatedListVehicleSizes));
};

/** Standalone refetch of /equipment/types. NOT called on page load — see /common/init. */
export const fetchEquipmentList = () => async (dispatch: any) => {
    dispatch(setIsVehicleTypeLoading(true));

    try {
        const response = await listEquipmentTypes({});

        applyEquipmentData(dispatch, response?.data);
    } catch (error) {
        console.error('Error fetching load link settings:', error);
    } finally {
        dispatch(setIsVehicleTypeLoading(false));
    }
};

/** Binds the /common/init `members` payload into Redux. */
export const applyTeamMemberListData = (dispatch: any, data: any[]) => {
    dispatch(setAllTeamMemberListOptions(Array.isArray(data) ? uniqBy(data, 'id') : []));
};

/**
 * Standalone refetch of /organization/members. NOT called on page load — see /common/init.
 * Kept as the init fallback for a backend that predates the `members` section, and available if a
 * page ever needs to force a resync. Day to day the list is kept current in place by
 * upsertTeamMembers / removeTeamMember from the Organization page.
 */
export const fetchAllTeamMemberListOptions = () => async (dispatch: any) => {
    dispatch(setIsTeamLoading(true));

    try {
        const response = await fetchOrganizationMemberList({ limit: 100 } as any);

        if (response?.data && response?.data?.length > 0) {
            applyTeamMemberListData(dispatch, response.data);
        }
    } finally {
        dispatch(setIsTeamLoading(false));
    }
};

/** Binds the /common/init `commodities` payload into Redux. */
export const applyCommoditiesData = (dispatch: any, data: any[]) => {
    if (Array.isArray(data) && data.length > 0) {
        dispatch(setCommodityOptions(getSelectBoxOptions(data, 'name', 'name')));
    } else {
        dispatch(setCommodityOptions([]));
    }
};

/** Standalone refetch of /loadboard-commodities/list. NOT called on page load — see /common/init. */
export const fetchCommodities = (params?: any) => async (dispatch: any) => {
    dispatch(setIsCommodityLoading(true));

    try {
        const response = await listLoadboardCommodities(params || {});

        applyCommoditiesData(dispatch, response?.data);
    } catch (error) {
        console.error('Error fetching commodities:', error);
        dispatch(setCommodityOptions([]));
    } finally {
        dispatch(setIsCommodityLoading(false));
    }
};

/**
 * Run room subscriptions after a successful socket connection.
 * Called both on first connect and on every auto-reconnect.
 */
const runSubscriptions = (socket: any, currentUser: any, dispatch: any) => {
    const subscriptions: Promise<void>[] = [];

    subscriptions.push(
        new Promise<void>((resolve) => {
            socket.get('/subscribe/globalRoom', (_data: any, jwr: any) => {
                if (jwr.error) {
                    console.error('Error subscribing to global room:', jwr.error);
                }
                resolve();
            });
        })
    );

    if (currentUser?.id) {
        subscriptions.push(
            new Promise<void>((resolve) => {
                socket.get(`/customer/subscribe/${currentUser.id}`, (_data: any, jwr: any) => {
                    if (jwr.error) {
                        console.error('Error subscribing to user room:', jwr.error);
                        dispatch(setIsUserRoomJoined(false));
                    } else {
                        dispatch(setIsUserRoomJoined(true));
                    }
                    resolve();
                });
            })
        );
    }

    Promise.all(subscriptions).then(() => {
        console.log('All socket subscriptions completed');
    });
};

/**
 * Connect to socket.io server.
 * Safe to call multiple times — duplicate calls are no-ops thanks to the
 * module-level isConnecting / socketInstance guards.
 *
 * Root cause of intermittent first-load failures (do not revert this design):
 * When sails.io.js loads dynamically after document.readyState === 'complete',
 * it immediately auto-connects using window.location.origin (the frontend host)
 * NOT window.WAL_API_URL (the backend). Patching io.socket._raw.io.uri after
 * the fact is unreliable — the in-progress connection to the wrong host is
 * already underway and _connect() may be a no-op.
 *
 * Solution: configure io.sails FIRST, tear down the auto-created socket silently,
 * then call io.sails.connect() to create a fresh socket that uses the correct URL
 * and auth headers from the very first connection attempt.
 */
export const connectSocket = () => async (dispatch: any, getState: () => RootState) => {
    try {
        const state = getState();
        const { isAuthenticated, currentUser } = state.auth;

        if (!isAuthenticated || !currentUser) {
            console.warn('connectSocket: user not authenticated, skipping');

            return;
        }

        // If our tracked socket is already live, sync Redux state and bail out
        if (socketInstance?.isConnected?.()) {
            dispatch(setIsSocketConnected(true));

            return;
        }

        // Prevent concurrent connection attempts (e.g. middleware + AppLayout firing together)
        if (isConnecting) {
            return;
        }

        isConnecting = true;

        const [io]: any = await fetchJsFromCDN('https://cdnjs.cloudflare.com/ajax/libs/sails.io.js/1.0.1/sails.io.min.js', ['io'], {
            // Suppress the "eager" socket sails.io.js opens on load. io.sails.url is still unset at
            // that point, so it dials window.location.origin — the app host, which runs no socket.io
            // server — and then retries forever (reconnectionAttempts is Infinity). Its disconnect()
            // throws for a socket that never connected, so the teardown below cannot clean it up.
            // With this off, sails.io.js deletes io.socket instead of connecting it, and the only
            // socket ever opened is the correctly-configured one from io.sails.connect() below.
            autoconnect: 'false',
            // sails.io.js uses this only to gate its own console logging — 'production' silences it.
            // Set explicitly because moving this tag into <body> otherwise lets the library infer it
            // from the .min.js URL. Keyed off window.MODE to match the rest of the app (Sidebar,
            // FilterModal, the quote forms); note window.MODE is only assigned for dev/sandbox builds
            // in public/index.html, so anything else falls through to 'development'.
            environment: window.MODE === 'production' ? 'production' : 'development',
        });

        // Configure io.sails BEFORE creating the socket so io.sails.connect() picks them up
        const token = localStorage.getItem('token');
        io.sails.url = window.WAL_API_URL;
        io.sails.transports = ['websocket', 'polling'];
        io.sails.withCredentials = true;
        io.sails.reconnection = true;
        io.sails.reconnectionAttempts = Infinity; // never give up
        io.sails.reconnectionDelay = 2000;
        io.sails.timeout = 10000;
        io.sails.useCORSRouteToGetCookie = false;
        io.sails.headers = {
            'ngrok-skip-browser-warning': 'true',
            ...(token && { Authorization: `Bearer ${token}` }),
        };

        // Tear down whatever socket exists — either the sails.io auto-created one
        // (connecting to the wrong URL) or a stale socket from a previous connectSocket() call.
        // We remove listeners first so disconnect() doesn't trigger our Redux handlers.
        const staleSocket = socketInstance || io.socket;

        if (staleSocket) {
            staleSocket.off('connect');
            staleSocket.off('disconnect');
            staleSocket.off('userStatusUpdate');

            try {
                staleSocket.disconnect();
            } catch (_e) {
                // Ignore errors on a socket we're discarding
            }
        }

        socketInstance = null;

        // Create a fresh socket using the io.sails config set above.
        // io.sails.connect() reads io.sails.url, io.sails.headers, etc.
        // and creates a new connection immediately — listeners attached below
        // will catch the 'connect' event since it fires asynchronously over the network.
        const socket = io.sails.connect();
        io.socket = socket;
        socketInstance = socket;

        // 'connect' fires on first connect AND on every sails.io auto-reconnect
        socket.on('connect', function socketConnected() {
            isConnecting = false;
            dispatch(setIsSocketConnected(true));
            dispatch(setSocketIo(socket));
            dispatch(setCustomerOnlineStatus(true));
            runSubscriptions(socket, currentUser, dispatch);
        });

        // On disconnect: update Redux but keep socketIo in Redux so components don't
        // lose their reference mid-reconnect. Reset isConnecting so the next AppLayout
        // retry is not blocked if connect never fired (e.g. server was down at first load).
        socket.on('disconnect', function socketDisconnected() {
            console.log('Socket disconnected — waiting for auto-reconnect');
            isConnecting = false;
            dispatch(setIsSocketConnected(false));
            dispatch(setCustomerOnlineStatus(false));
            dispatch(setIsUserRoomJoined(false));
        });

        socket.on('userStatusUpdate', function (data: any) {
            const status = data?.status || data?.isOnline || data?.online;
            const customerId = data?.customerId || data?.userId || data?.id;

            if (!customerId || customerId === currentUser?.id || customerId?.toString() === currentUser?.id?.toString()) {
                const isOnline = status === 'online' || status === true || status === 'Online';
                dispatch(setCustomerOnlineStatus(isOnline));
            }
        });
    } catch (error: any) {
        isConnecting = false;
        console.error('Error connecting socket:', error);
    }
};

export const disconnectSocket = () => async (dispatch: any) => {
    try {
        if (socketInstance) {
            // Remove listeners before disconnecting to prevent stale handlers
            socketInstance.off('connect');
            socketInstance.off('disconnect');
            socketInstance.off('userStatusUpdate');
            socketInstance.disconnect();
            socketInstance = null;
            console.log('Socket disconnected manually');
        }

        isConnecting = false;
        dispatch(setIsSocketConnected(false));
        dispatch(setSocketIo(null));
        dispatch(setIsAgentAvailable(false));
        dispatch(setIsUserRoomJoined(false));
    } catch (error: any) {
        console.error('Error disconnecting socket:', error);
    }
};

export const appearance = (state: RootState) => state.basic.appearance;

export default basicSlice.reducer;
